import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpResponseBase
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { NzModalService } from 'ng-zorro-antd/modal';
import { DA_SERVICE_TOKEN, ITokenService } from '@delon/auth';
import {
  ALAIN_I18N_TOKEN,
  IGNORE_BASE_URL,
  _HttpClient,
  RAW_BODY
} from '@delon/theme';
import { environment } from '@env/environment';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import {
  BehaviorSubject,
  Observable,
  of,
  throwError,
  catchError,
  filter,
  mergeMap,
  switchMap,
  take
} from 'rxjs';

const CODEMESSAGE: { [key: number]: string } = {
  0: 'Network error.',
  200: 'The request has succeeded.',
  201: 'The create request has succeeded.',
  202: 'The request has been received.',
  204: 'There is no content to send for this request.',
  400: 'Bad request',
  401: 'Unauthorized access.',
  403: 'Permission denied',
  404: 'Request not found.',
  406: 'Request format is invalid',
  410: 'The requested content has been permanently deleted',
  422: 'Unprocessable Entity',
  500: 'The server has encountered a situation.',
  502: 'Bad Gateway.',
  503: 'Service Unavailable.',
  504: 'Gateway Timeout.'
};

/**
 * 默认HTTP拦截器，其注册细节见 `app.module.ts`
 */
@Injectable()
export class DefaultInterceptor implements HttpInterceptor {
  private refreshTokenEnabled = environment.api.refreshTokenEnabled;
  private refreshTokenType: 're-request' | 'auth-refresh' =
    environment.api.refreshTokenType;
  private lastUpdate: number = new Date().getTime();
  private refreshingToken = false;
  private refreshToken$: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(private injector: Injector) {
    if (this.refreshTokenType === 'auth-refresh') {
      this.buildAuthRefresh();
    }
  }

  private get notification(): NzNotificationService {
    return this.injector.get(NzNotificationService);
  }

  private get msg(): NzMessageService {
    return this.injector.get(NzMessageService);
  }

  private get tokenSrv(): ITokenService {
    return this.injector.get(DA_SERVICE_TOKEN);
  }

  private get http(): _HttpClient {
    return this.injector.get(_HttpClient);
  }

  private goTo(url: string): void {
    setTimeout(() => this.injector.get(Router).navigateByUrl(url));
  }

  // private checkStatus(ev: HttpResponseBase): void {
  //   if (
  //     (ev.status >= 200 && ev.status < 300) ||
  //     ev.status === 401 ||
  //     ev.status === 400
  //   ) {
  //     return;
  //   }

  //   const errortext = CODEMESSAGE[ev.status] || ev.statusText;
  //   this.notification.error(`Request Error ${ev.status}: ${ev.url}`, errortext);
  // }

  /**
   * Refresh Token Request
   */
  private refreshTokenRequest(): Observable<any> {
    return this.http.post(`/api/auth/refresh`, null, { _silent: true });
  }

  // #region Refresh by error 401

  private tryRefreshToken(
    ev: HttpResponseBase,
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<any> {
    // 1、If the request is refresh token, redirect to login page directly
    if ([`/api/auth/refresh`].some(url => req.url.includes(url))) {
      this.toLogin();
      return throwError(() => ev);
    }
    // 2、Refresh token is in progress
    if (this.refreshingToken) {
      return this.refreshToken$.pipe(
        filter(v => !!v),
        take(1),
        switchMap(() => next.handle(this.reAttachToken(req)))
      );
    }
    // 3、Try to refresh token
    this.refreshingToken = true;
    this.refreshToken$.next(null);

    return this.refreshTokenRequest().pipe(
      switchMap(res => {
        // pipe to next request
        this.refreshingToken = false;
        this.refreshToken$.next(res);
        // save token
        this.tokenSrv.set(res);
        // resend request
        return next.handle(this.reAttachToken(req));
      }),
      catchError(err => {
        this.refreshingToken = false;
        this.toLogin();
        return throwError(() => err);
      })
    );
  }

  /**
   * 重新附加新 Token 信息
   *
   * > 由于已经发起的请求，不会再走一遍 `@delon/auth` 因此需要结合业务情况重新附加新的 Token
   */
  private reAttachToken(req: HttpRequest<any>): HttpRequest<any> {
    const token = this.tokenSrv.get()?.token;
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  // #endregion

  // #region 刷新Token方式二：使用 `@delon/auth` 的 `refresh` 接口

  private buildAuthRefresh(): void {
    if (!this.refreshTokenEnabled) {
      return;
    }
    this.tokenSrv.refresh
      .pipe(
        filter(() => !this.refreshingToken),
        switchMap(res => {
          this.refreshingToken = true;
          return this.refreshTokenRequest();
        })
      )
      .subscribe({
        next: res => {
          this.refreshingToken = false;
          this.tokenSrv.set(res);
        },
        error: () => this.toLogin()
      });
  }

  // #endregion

  private isOnPublicPortalRoute(): boolean {
    const url = this.injector.get(Router).url;
    return url.includes('full/einvoice') || url.includes('full/portal');
  }

  private toLogin(): void {
    if (window.location.href.indexOf(this.tokenSrv.login_url!) !== -1) return;
    if (this.isOnPublicPortalRoute()) return;

    // Close any open modals (e.g. License Reminder) before redirecting to login
    try {
      this.injector.get(NzModalService).closeAll();
    } catch {
      // ignore if modal service not available
    }
    this.goTo(this.tokenSrv.login_url!);
  }

  private handleData(
    ev: HttpResponseBase,
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<any> {
    const silent =
      req.context.get(SILENT) ||
      (ev.url && new RegExp(`[\?|&]_silent=[^&]+`).test(ev.url));
    const raw = ev.url && new RegExp(`[\?|&]_raw=[^&]+`).test(ev.url);
    // Handle Api Response
    switch (ev.status) {
      case 200:
        //  Failed Template：{ code: string, message: string, success: false }
        //  Success Template: { code: string, data: {  }, success: true }
        if (ev instanceof HttpResponse) {
          const body: any = ev.body;
          if (!body || !body.code) {
            return of(ev);
          }
          if (raw) {
            return of(ev);
          }

          this.attemptTokenRefresh(req.url.includes('api/app/qca'));
          if (body && !body.success) {
            var err = new HttpErrorResponse({
              error: ev.body,
              status: 400,
              url: `${ev.url}`
            });
            //if (!silent) {
            //   const errorMsg = this.getErrorMessage(body);
            //   if (errorMsg) this.msg.warning(errorMsg);
            //}
            return throwError(() => err);
          } else {
            if (req.context.get(RAW_BODY) || ev.body instanceof Blob) {
              return of(ev);
            }

            // Show success message for POST and DELETE requests
            let shouldShowSuccessMessage =
              body.message &&
              !silent &&
              (req.method === 'POST' || req.method === 'DELETE');
            if (shouldShowSuccessMessage) {
              const data = body.data;
              // Determine if the data is a response of devexpress grid data(has property "data", "totalCount", "groupCount")
              let isDevExpressGridData =
                data &&
                data.data &&
                data.totalCount !== undefined &&
                data.groupCount !== undefined;
              if (!isDevExpressGridData) {
                this.msg.success(body.message, { nzDuration: 3000 });
              }
            }
            return of(
              new HttpResponse(
                Object.assign(ev, { body: body.data, url: req.url })
              )
            );
          }
        }
        break;
      case 401:
        const token = this.tokenSrv.get();
        let isExpired =
          token && token?.expired
            ? new Date().getTime() > token['expired']
            : true;

        // there is a chance where unauthorized response is returned before the token is expired
        // while the token is being refreshed, prevent it from causing problems
        if (this.refreshingToken) {
          this.refreshingToken = false;
          break;
        }

        if (!silent && !isExpired) {
          // this.notification.error(`Unauthorized access, please login...`, ``);
        }
        if (
          this.refreshTokenEnabled &&
          this.refreshTokenType === 're-request'
        ) {
          return this.tryRefreshToken(ev, req, next);
        }
        this.toLogin();
        break;
      case 400:
        if (ev instanceof HttpErrorResponse) {
          console.warn('Bad Request', ev);

          if (silent)
            return of(
              new HttpResponse(Object.assign(ev, { body: {}, url: req.url }))
            );

          const error: any = ev.error;
          // Skip showing error message for NEEDS_CONFIRMATION
          if (error?.code === 'NEEDS_CONFIRMATION') {
            return throwError(() => ev);
          }
          // handle invalid ModelState error message
          if (error) {
            const errorMsg = this.getErrorMessage(error);
            if (errorMsg) this.msg.error(errorMsg);
          }

          return throwError(() => ev);
        }
        break;
      case 404:
        if (ev.url!.startsWith('http')) {
          throwError(() => ev);
        } else {
          console.warn(`Invalid URL: ${ev.url}`);
          this.goTo(`/exception/${ev.status}`);
        }
        break;
      case 403:
        if (ev.url!.startsWith('http')) {
          if (!silent) {
            const msg = (ev instanceof HttpErrorResponse && ev.error?.msg) 
              ? ev.error.msg 
              : 'You do not have permission to perform this action. Please contact your system administrator.';
            this.notification.error('Access Denied', msg);
          }
          return throwError(() => ev);
        } else this.goTo(`/exception/${ev.status}`);
        break;
      case 500:
        if (ev.url!.startsWith('http')) {
          if (!silent) {
            console.error(ev);
            if (ev instanceof HttpErrorResponse && ev.error && ev.error.msg)
              this.notification.error('Server Error', ev.error.msg);
            else
              this.notification.error('Server Error', CODEMESSAGE[ev.status]);
          }
          console.debug(ev);
          return throwError(() => ev);
        } else this.goTo(`/exception/${ev.status}`);
        break;
      default:
        if (ev instanceof HttpErrorResponse) {
          console.warn('Network Error', ev);
        }
        break;
    }
    if (ev instanceof HttpErrorResponse) {
      return throwError(() => ev);
    } else {
      // if (!ev.ok) {
      //   const errortext = CODEMESSAGE[ev.status] || ev.statusText;
      //   this.notification.error(`Request Error ${ev.status}: ${ev.url}`, errortext);
      // }
      return of(ev);
    }
  }

  private attemptTokenRefresh(isLogin: boolean) {
    // make sure parameters are re-initialized
    // when login is triggered
    if (isLogin) {
      this.refreshingToken = false;
      this.lastUpdate = new Date().getTime();
      return;
    }

    // convert milliseconds to minutes
    const elapsedMins = (new Date().getTime() - this.lastUpdate) / 60000;

    // if elapsed time is more than 15 minutes, refresh token
    if (elapsedMins > 15 && !this.refreshingToken) {
      this.refreshingToken = true;

      this.refreshTokenRequest().subscribe({
        next: (res: any) => {
          if (res) {
            this.refreshingToken = false;
            this.lastUpdate = new Date().getTime();
            this.tokenSrv.set({ ...res });
          }
        },
        error: () => {
          this.refreshingToken = false;
        }
      });
    }
  }

  private getAdditionalHeaders(headers?: HttpHeaders): {
    [name: string]: string;
  } {
    const res: { [name: string]: string } = {};
    const lang = this.injector.get(ALAIN_I18N_TOKEN).currentLang;
    if (!headers?.has('Accept-Language') && lang) {
      res['Accept-Language'] = lang;
    }

    return res;
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let url = req.url;
    // For non-api requests, do not append the api server url
    const isApiCall = url.indexOf('api/') !== -1;
    if (!isApiCall) {
      // Remove authorization header for WordPress API (qne.com.my and qne.cloud)
      if (
        url.startsWith('https://qne.com.my/wp-json/wp/v2/posts') ||
        url.startsWith('https://qne.cloud/')
      ) {
        req = req.clone({
          url,
          setHeaders: {
            Authorization: ''
          }
        });
      }
      return next.handle(req);
    }

    // Append the api server url to api call
    let serverUrl = environment.api.baseUrl;

    if (url.indexOf('api/reporting/') !== -1) {
      serverUrl = environment.api['reporting'];

      // If the url contains 'inquiry'
      // this is to redirect the inquiry request to secondary server
      if (url.indexOf('/inquiry') !== -1 || url.indexOf('/Inquiry') !== -1 && environment.api['reporting_mirror'])
        serverUrl = environment.api['reporting_mirror'];
    } else if (url.indexOf('api/uob/') !== -1) {
      // If it is UOB API, use the UOB API URL
      serverUrl =
        environment['stage'] === 'prod'
          ? environment.api['uobProd']
          : environment.api['uobUat'];

      // Fall back to the default API URL if the UOB API URL is not set
      if (!serverUrl) serverUrl = environment.api.baseUrl;
    }

    if (!serverUrl.endsWith('/')) serverUrl = `${serverUrl}/`;

    if (!req.context.get(IGNORE_BASE_URL) && !url.startsWith('http')) {
      url =
        serverUrl +
        (serverUrl.endsWith('/') && url.startsWith('/')
          ? url.substring(1)
          : url);
    }

    const newReq = req.clone({
      url,
      setHeaders: this.getAdditionalHeaders(req.headers)
    });
    return next.handle(newReq).pipe(
      mergeMap(ev => {
        if (ev instanceof HttpResponseBase) {
          return this.handleData(ev, newReq, next);
        }
        // Handle next
        return of(ev);
      }),
      catchError((err: HttpErrorResponse) => this.handleData(err, newReq, next))
    );
  }

  private getErrorMessage(errorBody: any) {
    if (errorBody.error && errorBody.error.innererror) {
      return errorBody.error.innererror.message;
    }
    if (errorBody.msg) return errorBody.msg;

    if (errorBody.message) return errorBody.message;

    let validationErrors = errorBody;
    if (errorBody.errors) {
      validationErrors = errorBody.errors;
    }
    const firstError = validationErrors[Object.keys(validationErrors)[0]];
    // check for import excel error correct message
    if(firstError[0] != "The input was not valid.") {
      return firstError[0];
    }
    const secondError = validationErrors[Object.keys(validationErrors)[1]];
    if(secondError)
      return secondError[0];
    else 
      return firstError[0];
  }
}

/**
 * Whether to execute the request silently
 * @example
 * this.http.post(`api/login`, {
 *  name: 'user', pwd: 'password'
 * }, {
 *  context: new HttpContext().set(SILENT, true)
 * })
 */
export const SILENT = new HttpContextToken(() => false);
